export const CUSTOM_FIELD_TEXT_MIN_LENGTH = 2;
export const CUSTOM_FIELD_TEXT_MAX_LENGTH = 100;
export const CUSTOM_FIELD_TEXTAREA_MAX_LENGTH = 150;

const OPTION_FIELD_TYPES = new Set([
  'multi_select',
  'select_dd',
  'select_radio',
]);

export type CustomFieldConfigForValidation = {
  fieldKey: string;
  fieldName: string;
  fieldType?: string;
  options?: string[] | null;
};

export type ValidateCustomFieldOptions = {
  requiredMessage?: string;
};

export function validateCustomFieldValue(
  field: CustomFieldConfigForValidation,
  value: string,
  options?: ValidateCustomFieldOptions
): string | null {
  const trimmed = value === undefined || value === null ? '' : String(value).trim();

  if (trimmed === '') {
    return options?.requiredMessage ?? `${field.fieldName} is required`;
  }

  if (field.fieldType === 'text') {
    if (trimmed.length < CUSTOM_FIELD_TEXT_MIN_LENGTH) {
      return `${field.fieldName} must be at least ${CUSTOM_FIELD_TEXT_MIN_LENGTH} characters`;
    }

    if (trimmed.length > CUSTOM_FIELD_TEXT_MAX_LENGTH) {
      return `${field.fieldName} must be at most ${CUSTOM_FIELD_TEXT_MAX_LENGTH} characters`;
    }

    return null;
  }

  if (field.fieldType === 'textarea') {
    if (trimmed.length < CUSTOM_FIELD_TEXT_MIN_LENGTH) {
      return `${field.fieldName} must be at least ${CUSTOM_FIELD_TEXT_MIN_LENGTH} characters`;
    }

    if (trimmed.length > CUSTOM_FIELD_TEXTAREA_MAX_LENGTH) {
      return `${field.fieldName} must be at most ${CUSTOM_FIELD_TEXTAREA_MAX_LENGTH} characters`;
    }

    return null;
  }

  if (
    field.fieldType &&
    OPTION_FIELD_TYPES.has(field.fieldType) &&
    field.options?.length
  ) {
    const allowedSet = new Set(
      field.options.map((o) => o.trim().toLowerCase())
    );

    if (field.fieldType === 'multi_select') {
      const parts = trimmed
        .split(',')
        .map((p) => p.trim().toLowerCase())
        .filter(Boolean);
      const invalid = parts.filter((p) => !allowedSet.has(p));

      if (invalid.length > 0) {
        return `${field.fieldName} must be one or more of: ${field.options.join(', ')}`;
      }
    } else if (!allowedSet.has(trimmed.toLowerCase())) {
      return `${field.fieldName} must be one of: ${field.options.join(', ')}`;
    }
  }

  return null;
}

export type CustomFieldConfigWithId = CustomFieldConfigForValidation & {
  id: string;
};

export type CustomFieldValueItem = {
  customFieldConfigId: string;
  value: string;
};

export type ValidateCustomFieldsForFormOptions = {
  requiredMessage?: string;
};

export function validateCustomFieldsForForm(
  fields: CustomFieldConfigWithId[],
  values: CustomFieldValueItem[],
  formOptions?: ValidateCustomFieldsForFormOptions
): Record<string, string> {
  const errors: Record<string, string> = {};
  const valueByConfigId = new Map(
    values.map((v) => [v.customFieldConfigId, v.value])
  );

  for (const field of fields) {
    const value = valueByConfigId.get(field.id) ?? '';
    const error = validateCustomFieldValue(
      {
        fieldKey: field.fieldKey,
        fieldName: field.fieldName,
        fieldType: field.fieldType,
        options: field.options ?? undefined,
      },
      value,
      { requiredMessage: formOptions?.requiredMessage }
    );

    if (error) {
      errors[field.fieldKey] = error;
    }
  }

  return errors;
}
